import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  CircleDot,
  ClipboardCheck,
  Clock3,
  FileClock,
  MessageSquarePlus,
  RadioTower,
  ShieldCheck,
  UserCheck,
  X,
} from "lucide-react";
import { listIncidents, updateIncident } from "../api";
import type { AuthUser, IncidentCase, Vehicle } from "../types";

const statusLabel = {
  new: "NUEVO",
  assigned: "ASIGNADO",
  in_progress: "EN ATENCIÓN",
  resolved: "RESUELTO",
  closed: "CERRADO",
};

export function IncidentPanel({
  open,
  onClose,
  vehicles,
  user,
  onSelectVehicle,
}: {
  open: boolean;
  onClose: () => void;
  vehicles: Vehicle[];
  user: AuthUser;
  onSelectVehicle: (vehicle: Vehicle) => void;
}) {
  const [rows, setRows] = useState<IncidentCase[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [resolution, setResolution] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const refresh = () => {
      listIncidents()
        .then((items) => {
          setRows(items);
          setSelectedId((current) =>
            items.some((item) => item.id === current) ? current : items[0]?.id ?? null,
          );
          setError("");
        })
        .catch((cause) =>
          setError(cause instanceof Error ? cause.message : "No fue posible cargar expedientes"),
        );
    };
    refresh();
    window.addEventListener("gsf:incident-updated", refresh);
    return () => window.removeEventListener("gsf:incident-updated", refresh);
  }, [open]);

  const selected = rows.find((row) => row.id === selectedId) || rows[0];
  const selectedVehicle = vehicles.find((vehicle) => vehicle.id === selected?.vehicle_id);
  const canOperate = ["superadmin", "fleet_admin", "operator", "dispatcher", "maintenance"].includes(user.role);
  const summary = useMemo(
    () => ({
      new: rows.filter((row) => row.status === "new").length,
      active: rows.filter((row) => ["assigned", "in_progress"].includes(row.status)).length,
      resolved: rows.filter((row) => ["resolved", "closed"].includes(row.status)).length,
    }),
    [rows],
  );

  async function apply(payload: Parameters<typeof updateIncident>[1]) {
    if (!selected) return;
    setPending(true);
    setError("");
    try {
      const updated = await updateIncident(selected.id, payload);
      setRows((current) => current.map((item) => item.id === updated.id ? updated : item));
      setNote("");
      setResolution("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No fue posible actualizar el expediente");
    } finally {
      setPending(false);
    }
  }

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside className="admin-panel incident-panel" role="dialog" aria-modal="true" aria-labelledby="incident-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div>
            <span className="eyebrow">MANDO DE INCIDENTES · TRAZABILIDAD OPERATIVA</span>
            <h2 id="incident-title">Expedientes C5</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar expedientes"><X size={18} /></button>
        </header>
        <section className="admin-panel__summary">
          <span><CircleDot size={17} /><b>{summary.new}</b> nuevos</span>
          <span><RadioTower size={17} /><b>{summary.active}</b> en atención</span>
          <span><CheckCircle2 size={17} /><b>{summary.resolved}</b> resueltos</span>
        </section>
        <div className="incident-panel__body">
          <section className="incident-queue">
            <div className="admin-section-title">
              <span className="eyebrow">BANDEJA DE EXPEDIENTES</span>
              <b>{rows.length} folios</b>
            </div>
            {rows.map((incident) => {
              const vehicle = vehicles.find((item) => item.id === incident.vehicle_id);
              return (
                <button
                  type="button"
                  className={`incident-item incident-item--${incident.priority}${selected?.id === incident.id ? " is-selected" : ""}`}
                  onClick={() => {
                    setSelectedId(incident.id);
                    setNote("");
                    setResolution("");
                  }}
                  key={incident.id}
                >
                  <FileClock size={16} />
                  <span>
                    <small>{incident.case_number} · {vehicle?.unit_code || "SISTEMA"}</small>
                    <strong>{incident.title}</strong>
                    <em>{incident.assigned_name || "Sin asignar"}</em>
                  </span>
                  <b>{statusLabel[incident.status]}</b>
                </button>
              );
            })}
            {!rows.length && <p className="maintenance-empty">SIN EXPEDIENTES REGISTRADOS</p>}
          </section>
          <section className="incident-detail">
            {selected && (
              <>
                <div className={`incident-badge incident-badge--${selected.priority}`}>
                  <ClipboardCheck size={25} />
                  <span><small>FOLIO OPERATIVO</small><b>{selected.case_number}</b></span>
                </div>
                <div className="incident-detail__title">
                  <span className="eyebrow">{selectedVehicle?.unit_code || "SISTEMA"} · {statusLabel[selected.status]}</span>
                  <h3>{selected.title}</h3>
                  <p>Abierto {new Date(selected.opened_at).toLocaleString("es-MX")}</p>
                </div>
                <div className="incident-owner">
                  <UserCheck size={18} />
                  <span><small>RESPONSABLE</small><strong>{selected.assigned_name || "Pendiente de asignación"}</strong></span>
                  {selected.acknowledged_at && <time>{new Date(selected.acknowledged_at).toLocaleTimeString("es-MX")}</time>}
                </div>
                <div className="incident-timeline">
                  <span className="eyebrow">BITÁCORA DEL CASO</span>
                  {selected.notes.map((entry) => (
                    <article key={entry.id}>
                      <Clock3 size={13} />
                      <span><strong>{entry.note}</strong><small>{entry.event_type} · {new Date(entry.created_at).toLocaleString("es-MX")}</small></span>
                    </article>
                  ))}
                </div>
                {canOperate && !["resolved", "closed"].includes(selected.status) && (
                  <div className="incident-controls">
                    <label>
                      NOTA DE SEGUIMIENTO
                      <textarea value={note} onChange={(event) => setNote(event.target.value)} placeholder="Contacto, validación o acción ejecutada..." />
                    </label>
                    <div>
                      {selected.status === "new" && <button type="button" disabled={pending} onClick={() => apply({ assign_to_me: true, note: note || undefined })}><UserCheck size={13} /> TOMAR CASO</button>}
                      {selected.status === "assigned" && <button type="button" disabled={pending} onClick={() => apply({ status: "in_progress", note: note || undefined })}><RadioTower size={13} /> INICIAR ATENCIÓN</button>}
                      {note.trim().length >= 3 && <button type="button" disabled={pending} onClick={() => apply({ note })}><MessageSquarePlus size={13} /> AGREGAR NOTA</button>}
                    </div>
                    {selected.status === "in_progress" && (
                      <label>
                        RESOLUCIÓN OBLIGATORIA
                        <textarea value={resolution} onChange={(event) => setResolution(event.target.value)} placeholder="Resultado, evidencia y cierre operativo..." />
                        <button type="button" disabled={pending || resolution.trim().length < 3} onClick={() => apply({ status: "resolved", resolution, note: note || undefined })}><ShieldCheck size={13} /> RESOLVER EXPEDIENTE</button>
                      </label>
                    )}
                  </div>
                )}
                {selected.resolution && <div className="incident-resolution"><ShieldCheck size={18} /><span><small>RESOLUCIÓN DOCUMENTADA</small><strong>{selected.resolution}</strong></span></div>}
                {selectedVehicle && (
                  <button type="button" className="primary-button" onClick={() => { onSelectVehicle(selectedVehicle); onClose(); }}>
                    ABRIR UNIDAD <ArrowRight size={14} />
                  </button>
                )}
              </>
            )}
          </section>
        </div>
        {error && <div className="admin-message admin-message--error">{error}</div>}
      </aside>
    </div>
  );
}
